import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { cubicOut } from "svelte/easing";
import type { TransitionConfig } from "svelte/transition";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type FlyAndScaleParams = {
  y?: number;
  x?: number;
  start?: number;
  duration?: number;
};

export const flyAndScale = (
  node: Element,
  params: FlyAndScaleParams = { y: -8, x: 0, start: 0.95, duration: 150 },
): TransitionConfig => {
  const style = getComputedStyle(node);
  const transform = style.transform === "none" ? "" : style.transform;

  const scaleConversion = (valueA: number, scaleA: [number, number], scaleB: [number, number]) => {
    const [minA, maxA] = scaleA;
    const [minB, maxB] = scaleB;

    const percentage = (valueA - minA) / (maxA - minA);
    const valueB = percentage * (maxB - minB) + minB;

    return valueB;
  };

  const styleToString = (style: Record<string, number | string | undefined>): string => {
    return Object.keys(style).reduce((str, key) => {
      if (style[key] === undefined) return str;
      return str + `${key}:${style[key]};`;
    }, "");
  };

  return {
    duration: params.duration ?? 200,
    delay: 0,
    css: (t) => {
      const y = scaleConversion(t, [0, 1], [params.y ?? 5, 0]);
      const x = scaleConversion(t, [0, 1], [params.x ?? 0, 0]);
      const scale = scaleConversion(t, [0, 1], [params.start ?? 0.95, 1]);

      return styleToString({
        transform: `${transform} translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        opacity: t,
      });
    },
    easing: cubicOut,
  };
};

export const debounce = (func: (...args: any[]) => void, timeout = 300) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), timeout);
  };
};

export const getHtml2Pdf = async () => {
  if (typeof window === "undefined") return null; // SSR guard

  if (!window.html2pdf) {
    await new Promise<void>((resolve) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
  }

  return window.html2pdf;
};

export interface PdfExportOptions {
  margin?: number;
  filename?: string;
  image?: { type: string; quality: number };
  html2canvas?: { scale: number; useCORS: boolean };
  jsPDF?: { unit: string; format: string; orientation: string };
}

export interface PdfContent {
  content: string;
  filename: string;
}

export const exportToPdf = async (content: string | PdfContent[], options: PdfExportOptions = {}): Promise<void> => {
  if (typeof window === "undefined") return; // SSR guard

  const html2pdf = await getHtml2Pdf();
  if (!html2pdf) {
    throw new Error("PDF generation library not available");
  }

  const defaultOptions: PdfExportOptions = {
    margin: 15,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };

  const finalOptions = { ...defaultOptions, ...options };

  const parseMarkdown = async (content: string): Promise<string> => {
    const MarkdownIt = (await import("markdown-it")).default;
    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: true,
    });

    const isMarkdown =
      content.includes("#") ||
      content.includes("**") ||
      content.includes("```") ||
      content.includes("- ") ||
      content.includes("1. ") ||
      (content.includes("[") && content.includes("](")) ||
      content.includes("|---|");

    if (isMarkdown) {
      return md.render(content);
    }

    return content;
  };

  const createPdfContainer = async (content: string): Promise<HTMLDivElement> => {
    const container = document.createElement("div");
    container.className = "pdf-export-container";

    const style = document.createElement("style");
    style.textContent = `
      .pdf-export-container h1 { font-size: 24px; font-weight: bold; margin: 20px 0; }
      .pdf-export-container h2 { font-size: 20px; font-weight: bold; margin: 16px 0; }
      .pdf-export-container h3 { font-size: 18px; font-weight: bold; margin: 14px 0; }
      .pdf-export-container h4 { font-size: 16px; font-weight: bold; margin: 12px 0; }
      .pdf-export-container p { margin: 10px 0; line-height: 1.5; }
      .pdf-export-container ul { 
        margin: 10px 0; 
        padding-left: 20px;
        list-style-type: disc;
      }
      .pdf-export-container ol { 
        margin: 10px 0; 
        padding-left: 20px;
        list-style-type: decimal;
      }
      .pdf-export-container li { 
        margin: 5px 0;
        display: list-item;
      }
      .pdf-export-container pre { background: #f0f0f0; padding: 8px; border-radius: 4px; white-space: pre-wrap; }
      .pdf-export-container code { font-family: monospace; background: #f0f0f0; padding: 2px 4px; border-radius: 2px; }
      .pdf-export-container br { display: block; margin: 8px 0; }
      .pdf-export-container hr { border: 1px solid #ddd; margin: 16px 0; }
      .pdf-export-container table { 
        border-collapse: collapse;
        width: 100%;
        margin: 16px 0;
      }
      .pdf-export-container th, .pdf-export-container td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      .pdf-export-container th {
        background-color: #f5f5f5;
        font-weight: bold;
      }
      .pdf-export-container blockquote {
        border-left: 4px solid #ddd;
        padding-left: 16px;
        margin: 16px 0;
        color: #666;
      }
    `;

    container.appendChild(style);

    const contentDiv = document.createElement("div");
    const parsedContent = await parseMarkdown(content);
    contentDiv.innerHTML = parsedContent;
    container.appendChild(contentDiv);

    return container;
  };

  const cleanup = (container: HTMLDivElement) => {
    container.remove();
  };

  if (Array.isArray(content)) {
    const JSZip = await import("jszip");
    const zip = new JSZip.default();

    for (const item of content) {
      const container = await createPdfContainer(item.content);
      const pdf = await html2pdf()
        .from(container)
        .set({ ...finalOptions, filename: item.filename })
        .output("blob");

      zip.file(`${item.filename}.pdf`, pdf);
      cleanup(container);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "exported_pdfs.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } else {
    const container = await createPdfContainer(content);
    await html2pdf().from(container).set(finalOptions).save();
    cleanup(container);
  }
};
